import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ManageGptsView } from '../Components/ExploreGpt/ManageGptsView';
import { GptSummaryItem } from '../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { buildRoute, ManageGptsRoute, PersonalGptSpecificRoute } from '../Routes';
import ShareGPT from '../Components/ShareGPT/ShareGPT';
import { performEnterpriseGPTApiCall } from '../Hooks/useMiddlewareApiCall';
import { useNotification } from '../Contexts/NotificationContext';
import { useGptConfigurations, useFeature } from '../store/hooks';
import { editGptConfiguration } from '../store/actions/gptConfigurationsAction';

export const PageManageGpts: FC = () => {
    const navigate = useNavigate();
    const { dispatchNotification } = useNotification();
    const { gptConfigurations, dispatch: gptDispatch } = useGptConfigurations();
    const chatFeature = useFeature('chat');
    const [shareGptId, setShareGptId] = useState<string | null>(null);

    const handleEdit = (gpt: GptSummaryItem) => {
        if (gpt.id) {
            navigate(buildRoute(PersonalGptSpecificRoute, { id: gpt.id }), { state: { from: ManageGptsRoute, useOwnerApi: true } });
        }
    };

    const handleShare = (gpt: GptSummaryItem) => {
        setShareGptId(gpt.id!);
    };

    const handleShareClose = () => {
        setShareGptId(null);
    };

    const handleShareSubmit = (gptId: string, emails: string[], _message: string) => {
        if (!emails.length) return;

        performEnterpriseGPTApiCall(async (client, abortSignal) => {
            try {
                const currentGptData = await client.getAnyGpt(gptId, abortSignal);
                const existingEmails = currentGptData.sharedWith || [];
                const newEmails = emails.filter((email) => !existingEmails.includes(email));
                const updatedSharedWith = [...existingEmails, ...newEmails];

                await client.modifyPersonalGpt(
                    gptId,
                    currentGptData.name,
                    currentGptData.description,
                    currentGptData.instruction,
                    [],
                    [],
                    [],
                    updatedSharedWith,
                    currentGptData.isPublic || false,
                    currentGptData.isDuplicable || false,
                    abortSignal,
                );

                const existingGpt = gptConfigurations.find((g) => g.id === gptId);
                if (existingGpt) {
                    gptDispatch(editGptConfiguration({ ...existingGpt, numberOfShares: updatedSharedWith.length }));
                }

                dispatchNotification('GPT shared successfully!', 'success');
            } catch (error) {
                console.error('Error sharing GPT:', error);
                dispatchNotification('Failed to share GPT. Please try again.', 'error');
            }
        });
    };

    const handleRemoveShare = async (gptId: string, emailToRemove: string): Promise<void> => {
        return performEnterpriseGPTApiCall(async (client, abortSignal) => {
            try {
                const currentGptData = await client.getAnyGpt(gptId, abortSignal);
                const updatedSharedWith = (currentGptData.sharedWith || []).filter((email) => email !== emailToRemove);

                await client.modifyPersonalGpt(
                    gptId,
                    currentGptData.name,
                    currentGptData.description,
                    currentGptData.instruction,
                    [],
                    [],
                    [],
                    updatedSharedWith,
                    currentGptData.isPublic || false,
                    currentGptData.isDuplicable || false,
                    abortSignal,
                );

                const existingGpt = gptConfigurations.find((g) => g.id === gptId);
                if (existingGpt) {
                    gptDispatch(editGptConfiguration({ ...existingGpt, numberOfShares: updatedSharedWith.length }));
                }

                dispatchNotification('User removed successfully!', 'success');
            } catch (error) {
                console.error('Error removing user from GPT:', error);
                dispatchNotification('Failed to remove user. Please try again.', 'error');
            }
        });
    };

    const handleSetPublicStatus = async (gptId: string, isPublic: boolean): Promise<void> => {
        return performEnterpriseGPTApiCall(async (client, abortSignal) => {
            try {
                const currentGptData = await client.getAnyGpt(gptId, abortSignal);

                await client.modifyPersonalGpt(
                    gptId,
                    currentGptData.name,
                    currentGptData.description,
                    currentGptData.instruction,
                    [],
                    [],
                    [],
                    currentGptData.sharedWith || [],
                    isPublic,
                    currentGptData.isDuplicable || false,
                    abortSignal,
                );

                const existingGpt = gptConfigurations.find((g) => g.id === gptId);
                if (existingGpt) {
                    gptDispatch(editGptConfiguration({ ...existingGpt, isPublic }));
                }

                dispatchNotification(isPublic ? 'GPT is now public to the organization!' : 'GPT is now private!', 'success');
            } catch (error) {
                console.error('Error updating GPT public status:', error);
                dispatchNotification(`Failed to make GPT ${isPublic ? 'public' : 'private'}. Please try again.`, 'error');
            }
        });
    };

    return (
        <>
            <ManageGptsView onGptEdit={handleEdit} onGptShare={chatFeature.personalGpt?.hasRightToShareGpt ? handleShare : undefined} />
            {shareGptId && (
                <ShareGPT
                    open={true}
                    gptId={shareGptId}
                    useOwnerApi={true}
                    onClose={handleShareClose}
                    onShare={handleShareSubmit}
                    onRemove={handleRemoveShare}
                    onSetPublicStatus={handleSetPublicStatus}
                />
            )}
        </>
    );
};
